import React, { useRef, useState } from "react";
import { Ear } from "lucide-react";
import { api } from "../lib/api";
import { useWakeWord } from "../lib/useWakeWord";
import { getVoice } from "../lib/voicePref";
import { toast } from "sonner";

const b64ToBlob = (b64, mime = "audio/mpeg") => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

/**
 * Persistent floating wake indicator. Listens for "hey sally" and sends the
 * follow-on speech straight to the assistant chat endpoint (text-in, TTS-out).
 * Enabled state persists in localStorage. Only mounts if browser supports it.
 */
const WakeSally = () => {
  const supported = typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  const [enabled, setEnabled] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("op:wake:enabled") === "1"
  );
  const [bubble, setBubble] = useState(null); // {q, reply}
  const audioRef = useRef(null);
  const [sessionId, setSessionId] = useState(null);

  const onQuestion = async (q) => {
    try {
      const res = await api.assistantChat({ message: q, session_id: sessionId, speak: true, voice: getVoice() });
      setSessionId(res.session_id);
      setBubble({ q, reply: res.reply });
      if (res.audio_b64 && audioRef.current) {
        audioRef.current.src = URL.createObjectURL(b64ToBlob(res.audio_b64));
        audioRef.current.play().catch(() => {});
      }
      setTimeout(() => setBubble(null), 22000);
    } catch (e) {
      toast("Sally couldn't answer", { description: e?.response?.data?.detail || "" });
    }
  };

  const wake = useWakeWord({ wakePhrase: "hey sally", enabled: !!supported && enabled, onQuestion });

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem("op:wake:enabled", next ? "1" : "0");
    if (next) toast('Listening for "Hey Sally"');
    else toast("Sally is asleep");
  };

  if (!supported) return null;

  return (
    <>
      <button
        onClick={toggle}
        data-testid="wake-toggle"
        data-state={wake.state}
        className={`fixed bottom-24 md:bottom-8 right-[288px] md:right-[312px] z-40 flex items-center gap-2 pl-3 pr-4 py-3 border transition-all select-none ${
          enabled && wake.state === "capturing"
            ? "bg-[rgba(245,166,35,0.15)] border-[#F5A623] text-[#F5A623] op-p1-flash"
            : enabled && wake.state === "listening"
            ? "bg-[rgba(0,229,255,0.06)] border-[rgba(0,229,255,0.35)] text-[#00E5FF]"
            : "bg-[#0A0C14]/90 backdrop-blur-md border-white/10 text-[#5E657D] hover:text-white hover:border-white/25"
        }`}
        title={enabled ? "Wake word active — say 'Hey Sally'" : "Click to enable 'Hey Sally' wake word"}
      >
        <Ear size={14} />
        <span className="op-mono text-[10px] tracking-[0.24em]">
          {!enabled ? "SALLY · OFF" : wake.state === "capturing" ? "LISTENING…" : wake.state === "error" ? "SALLY · ERR" : "HEY SALLY"}
        </span>
      </button>

      {bubble && (
        <div
          data-testid="wake-bubble"
          className="fixed bottom-[152px] md:bottom-[80px] right-6 z-40 max-w-sm op-panel op-panel--glow p-4"
        >
          <div className="op-tag mb-1 text-[#00E5FF]">SALLY</div>
          <div className="text-white text-sm leading-relaxed">{bubble.reply}</div>
          {bubble.q && <div className="op-tag mt-2 text-[#5E657D]">YOU: "{bubble.q}"</div>}
        </div>
      )}
      <audio ref={audioRef} className="hidden" data-testid="wake-audio" />
    </>
  );
};

export default WakeSally;
